import React, { useMemo } from "react";
import { Box, Typography, Chip, Checkbox } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/AccessTime";

import { formatDate } from "../../utils/dateUtils";
import { getGridSingleSelectOperators } from "@mui/x-data-grid-premium";

import WIPCommentIcon from "./WIPCommentIcon.component";

// PERFORMANCE OPTIMIZATION: Date parsing cache
// Cache parsed dates to avoid re-parsing the same values
const dateCache = new Map();
const MAX_CACHE_SIZE = 1000; // Limit cache size to prevent memory issues

// Helper to get cache key
const getCacheKey = (rowId, field, value) => {
  return `${rowId}_${field}_${value}`;
};

// Clear cache when it gets too large
const clearCacheIfNeeded = () => {
  if (dateCache.size > MAX_CACHE_SIZE) {
    // Clear oldest 50% of entries
    const entries = Array.from(dateCache.entries());
    const toDelete = entries.slice(0, Math.floor(entries.length / 2));
    toDelete.forEach(([key]) => dateCache.delete(key));
  }
};

// Approval status options
const APPROVAL_STATUS_OPTIONS = [
  { value: "", label: "-- Select --" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "Pending", label: "Pending" },
  { value: "In Review", label: "In Review" },
  { value: "Conditional Approval", label: "Conditional Approval" },
];

// Test conclusion options
const TEST_CONCLUSION_OPTIONS = [
  { value: "", label: "-- Select --" },
  { value: "Pass", label: "Pass" },
  { value: "Fail", label: "Fail" },
  { value: "Pending", label: "Pending" },
  { value: "In Progress", label: "In Progress" },
];

// Helper function to extract substage display name from field name
// Uses displayName stored in flat row: "stageKey: substageKey (DISPLAY_NAME)"
// Example: "fabric_approval: lab_dips_submit (REQ)" -> "LAB DIPS/STRIKE OFF/ KNIT DOWN SUBMIT (REQ)"
const getHeaderNameFromField = (field, row) => {
  // Extract the part after the colon and before the type suffix
  const colonIndex = field.indexOf(":");
  if (colonIndex === -1) {
    // No colon, return field as-is (for main info fields)
    return field;
  }

  // Extract stageKey and substageKey from field name
  const stageKey = field.substring(0, colonIndex).trim();
  const afterColon = field.substring(colonIndex + 1).trim();
  const parenIndex = afterColon.lastIndexOf("(");

  if (parenIndex > 0) {
    const substageKey = afterColon.substring(0, parenIndex).trim();
    const fieldType = afterColon
      .substring(parenIndex + 1, afterColon.length - 1)
      .trim();

    // Get displayName from flat row if available
    if (row) {
      const displayNameField = `${stageKey}: ${substageKey} (DISPLAY_NAME)`;
      const displayName = row[displayNameField];

      if (displayName) {
        return `${displayName} (${fieldType})`;
      }
    }

    // Fallback: return substageKey if displayName not found
    return `${substageKey} (${fieldType})`;
  }

  // Return as-is if parsing fails
  return afterColon;
};

// Create base column with common settings
const createBaseColumn = (field, headerName, options = {}) => {
  const {
    onColumnHeaderCheckbox,
    isColumnFullySelected,
    onCellSelection,
    isCellSelected,
    getSelectedCount,
    ...restOptions
  } = options;

  const baseColumn = {
    field,
    headerName: headerName || getHeaderNameFromField(field, null),
    width: 150,
    editable: true,
    ...restOptions,
  };

  // Add header checkbox if handlers are provided
  if (onColumnHeaderCheckbox && isColumnFullySelected && getSelectedCount) {
    baseColumn.renderHeader = (params) => {
      const isSelected = isColumnFullySelected(field);
      const selectedCount = getSelectedCount(field);
      const hasPartialSelection = selectedCount > 0 && !isSelected;

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            height: "100%",
          }}
        >
          <Checkbox
            size="small"
            checked={isSelected}
            indeterminate={hasPartialSelection}
            onChange={(e) => {
              e.stopPropagation();
              onColumnHeaderCheckbox(field, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{ padding: "4px" }}
          />
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: "12px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {headerName || params.colDef.headerName}
          </Typography>
        </Box>
      );
    };
  }

  // Add cell checkbox if handlers are provided
  // Position checkbox on the left, cell content on the right
  // Checkbox click stops propagation to prevent cell editing
  if (onCellSelection && isCellSelected) {
    // Store original renderCell if it exists (from restOptions or already set)
    const originalRenderCell = baseColumn.renderCell || restOptions.renderCell;
    baseColumn.renderCell = (params) => {
      const cellSelected = isCellSelected(field, params.row.id);

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            width: "100%",
            height: "100%",
            position: "relative",
          }}
          onClick={(e) => {
            // Don't prevent cell editing if clicking on the content area
            if (e.target.closest(".MuiCheckbox-root")) {
              e.stopPropagation();
            }
          }}
        >
          <Checkbox
            size="small"
            checked={cellSelected}
            onChange={(e) => {
              e.stopPropagation();
              onCellSelection(field, params.row.id, e.target.checked);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            sx={{
              padding: "2px",
              flexShrink: 0,
              pointerEvents: "auto",
            }}
            className="cell-selection-checkbox"
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              pointerEvents: "auto",
            }}
          >
            {originalRenderCell
              ? originalRenderCell(params)
              : (() => {
                  // Handle Date objects - formatDate handles Date objects directly
                  if (params.value instanceof Date) {
                    return formatDate(params.value);
                  }
                  // Handle null/undefined
                  if (params.value === null || params.value === undefined) {
                    return "";
                  }
                  // Return as string for other types
                  return String(params.value);
                })()}
          </Box>
        </Box>
      );
    };
  }

  return baseColumn;
};

// Create date column
const createDateColumn = (
  field,
  headerName,
  handleOpenComments,
  options = {}
) => ({
  ...createBaseColumn(field, headerName, { width: 150, ...options }),
  type: "date",
  // Note: editable is already set to true in createBaseColumn
  // No custom renderEditCell - let DataGrid handle it with default date editor
  valueGetter: (value, row) => {
    // PERFORMANCE OPTIMIZATION: Use cache for date parsing
    // MUI DataGrid v6+ uses signature: valueGetter(value, row)
    // value is the raw field value from row[field]

    // Handle null/empty
    if (value === null || value === undefined || value === "") {
      return null;
    }

    // If already a Date object, return it
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    // Check cache first
    const rowId = row?.id || row?._id || "";
    const cacheKey = getCacheKey(rowId, field, value);
    if (dateCache.has(cacheKey)) {
      return dateCache.get(cacheKey);
    }

    // Try to parse as date string (ISO format from backend)
    let parsedDate = null;
    if (typeof value === "string") {
      // If it's an ISO string with time (e.g., "2025-11-11T05:00:00.000Z")
      // Extract just the date part to avoid timezone conversion
      const datePart = value.split("T")[0];
      if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = datePart.split("-");
        // Create date in UTC to avoid timezone conversion
        parsedDate = new Date(
          Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day))
        );
      } else {
        // Fallback for other date formats
        parsedDate = new Date(value);
        if (isNaN(parsedDate.getTime())) {
          parsedDate = null;
        }
      }
    }

    // Cache the result
    if (parsedDate && !isNaN(parsedDate.getTime())) {
      dateCache.set(cacheKey, parsedDate);
      clearCacheIfNeeded();
    } else {
      dateCache.set(cacheKey, null);
      clearCacheIfNeeded();
    }

    return parsedDate;
  },
  valueSetter: (value, row) => {
    // MUI DataGrid v6+ uses signature: valueSetter(value, row)
    // NOT valueSetter(params) with params.value and params.row
    console.log(`[DateColumn valueSetter] ${field}:`, {
      value,
      rowId: row?.id,
    });

    if (!row) {
      console.warn(`[DateColumn valueSetter] No row provided for ${field}`);
      return row; // Return original row unchanged
    }

    // Create updated row - MUST preserve all row properties
    const newRow = { ...row };

    // Update the specific field with the new date value
    if (value instanceof Date) {
      // DataGrid passes Date object - convert to ISO string for Redux
      newRow[field] = isNaN(value.getTime()) ? null : value.toISOString();
    } else if (value === null || value === undefined || value === "") {
      // Clear the date
      newRow[field] = null;
    } else if (typeof value === "string" && value.trim()) {
      // Validate string date
      const dateValue = new Date(value);
      newRow[field] = !isNaN(dateValue.getTime()) ? value : null;
    } else {
      // Fallback - store as-is
      newRow[field] = value;
    }

    // CRITICAL: Return the updated row object
    return newRow;
  },
  valueFormatter: (value, row, column, apiRef) => {
    // MUI DataGrid v6+ uses signature: valueFormatter(value, row, column, apiRef)
    // value comes from valueGetter (should be a Date object)

    if (!value) return "";

    // Handle Date objects (from valueGetter)
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return "";
      return formatDate(value.toISOString());
    }

    // Handle ISO strings (fallback if valueGetter didn't convert)
    if (typeof value === "string") {
      const dateValue = new Date(value);
      if (isNaN(dateValue.getTime())) return "";
      return formatDate(value);
    }

    return "";
  },
  // PERFORMANCE OPTIMIZATION: Remove renderCell - valueFormatter is sufficient
  // renderCell removed - using valueFormatter instead for better performance
});

// Create status column (dropdown) - simplified, no comment icon
const createStatusColumn = (field, headerName, options = {}) => {
  // Handle both array (legacy) and object (with selection handlers) formats
  const valueOptions = Array.isArray(options)
    ? options
    : options.valueOptions || APPROVAL_STATUS_OPTIONS;
  const { valueOptions: _, ...restOptions } = Array.isArray(options)
    ? {}
    : options;

  return {
    ...createBaseColumn(field, headerName, { width: 160, ...restOptions }),
    type: "singleSelect",
    valueOptions: valueOptions,
    editable: true,
    valueGetter: (value, row) => {
      // Normalize status value to match dropdown options
      // Backend sends lowercase values like "approved", "rejected", "pending"
      // Dropdown options use capitalized values like "Approved", "Rejected", "Pending"
      if (!value || typeof value !== "string") {
        return value || "";
      }

      // Find matching option by case-insensitive comparison
      const normalizedValue = value.toLowerCase();
      const matchingOption = valueOptions.find(
        (opt) => opt.value && opt.value.toLowerCase() === normalizedValue
      );

      // Return the option's value (capitalized) if found, otherwise return original value
      return matchingOption ? matchingOption.value : value;
    },
  };
};

// Create approval dropdown column (uses APPROVAL_STATUS_OPTIONS by default)
const createApprovalDropdownColumn = (field, headerName, options = {}) => ({
  ...createBaseColumn(field, headerName, { width: 160, ...options }),
  type: "singleSelect",
  valueOptions: APPROVAL_STATUS_OPTIONS,
  editable: true,
});

// Create status chip column (read-only display for supplier-facing platform)
const createStatusChipColumn = (field, headerName, options = {}) => {
  // Map status values to chip colors
  const getStatusColor = (status) => {
    if (!status || typeof status !== "string") {
      return "default"; // For hyphen display
    }

    const normalizedStatus = status.toLowerCase().trim();

    switch (normalizedStatus) {
      case "approved":
        return "success"; // Green
      case "rejected":
        return "error"; // Red
      case "conditional approval":
        return "warning"; // Orange
      case "in review":
        return "info"; // Yellow/Blue (using info for yellow-like color)
      case "pending":
        return "default"; // White/Gray
      default:
        return "default";
    }
  };

  // Normalize status value for display
  const normalizeStatusValue = (value) => {
    if (!value || typeof value !== "string") {
      return null;
    }

    const normalized = value.toLowerCase().trim();

    // Map to display values
    const statusMap = {
      approved: "Approved",
      rejected: "Rejected",
      "conditional approval": "Conditional Approval",
      "in review": "In Review",
      pending: "Pending",
    };

    return statusMap[normalized] || value; // Return capitalized version if found, otherwise original
  };

  // Custom renderCell for status chips
  const statusChipRenderCell = (params) => {
      const value = params?.value;

      // Display hyphen if no value
      if (!value || value === "" || value === null || value === undefined) {
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          </Box>
        );
      }

      const statusValue = typeof value === "string" ? value : String(value);
      const displayValue = normalizeStatusValue(statusValue);

      // Custom color mapping for specific statuses
      let chipBgColor = undefined;
      let chipTextColor = undefined;

      const normalizedStatus = statusValue.toLowerCase().trim();

      if (normalizedStatus === "approved") {
        chipBgColor = "#4caf50"; // Green
        chipTextColor = "#ffffff";
      } else if (normalizedStatus === "rejected") {
        chipBgColor = "#f44336"; // Red
        chipTextColor = "#ffffff";
      } else if (
        normalizedStatus === "conditional approval" ||
        normalizedStatus === "conditionalapproval"
      ) {
        chipBgColor = "#ff9800"; // Orange
        chipTextColor = "#ffffff";
      } else if (
        normalizedStatus === "in review" ||
        normalizedStatus === "inreview"
      ) {
        chipBgColor = "#ffeb3b"; // Yellow
        chipTextColor = "#000000";
      } else if (normalizedStatus === "pending") {
        chipBgColor = "#ffffff"; // White
        chipTextColor = "#000000";
      } else {
        // Default: gray for unknown statuses
        chipBgColor = "#e0e0e0";
        chipTextColor = "#000000";
      }

      return (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip
            label={displayValue}
            size="small"
            sx={{
              backgroundColor: chipBgColor,
              color: chipTextColor,
              fontWeight: 500,
              border: chipBgColor === "#ffffff" ? "1px solid #e0e0e0" : "none", // Add border for white chip
            }}
          />
        </Box>
      );
  };

  return {
    ...createBaseColumn(field, headerName, {
      width: 160,
      renderCell: statusChipRenderCell,
      ...options,
    }),
    editable: false, // Always read-only for chips
    type: undefined, // Remove any type (don't use singleSelect for chips)
    valueGetter: (value, row) => {
      // Normalize status value to match chip display
      if (!value || typeof value !== "string") {
        return null;
      }

      const normalizedValue = value.toLowerCase();
      const statusMap = {
        approved: "Approved",
        rejected: "Rejected",
        "conditional approval": "Conditional Approval",
        "in review": "In Review",
        pending: "Pending",
      };

      return statusMap[normalizedValue] || value;
    },
  };
};

// PERFORMANCE OPTIMIZATION: Memoized comment cell component
const CommentCell = React.memo(({ row, field, handleOpenComments }) => {
  const rawValue = row[field];
  const commentCount = Array.isArray(rawValue)
    ? rawValue.length
    : rawValue
    ? 1
    : 0;

  // Get latest comment text (last index in array)
  const latestCommentText =
    Array.isArray(rawValue) && rawValue.length > 0
      ? rawValue[rawValue.length - 1]?.comment || ""
      : "";

  // Truncate to maximum 3 lines of 70 characters each (210 characters total)
  const maxLength = 210;
  const truncatedText =
    latestCommentText.length > maxLength
      ? latestCommentText.substring(0, maxLength) + "..."
      : latestCommentText;

  const handleClick = React.useCallback(() => {
    if (handleOpenComments) {
      handleOpenComments(row, field);
    }
  }, [row, field, handleOpenComments]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        width: "100%",
        py: 0.5,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.875rem",
          flex: 1,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          lineHeight: 1.4,
          maxHeight: "3.92rem", // Approximately 3 lines (0.875rem * 1.4 * 3)
          wordBreak: "break-word",
        }}
      >
        {truncatedText}
      </Typography>
      <Box sx={{ flexShrink: 0, pt: 0.25 }}>
        <WIPCommentIcon commentCount={commentCount} onClick={handleClick} />
      </Box>
    </Box>
  );
});

CommentCell.displayName = "CommentCell";

// Create text column with comments
const createTextColumnWithComments = (
  field,
  headerName,
  handleOpenComments,
  options = {}
) => {
  // Custom renderCell for comment columns
  const commentRenderCell = (params) => {
    if (!params || !params.row) return "";
    return (
      <CommentCell
        row={params.row}
        field={field}
        handleOpenComments={handleOpenComments}
      />
    );
  };

  return {
    ...createBaseColumn(field, headerName, {
      width: 200,
      renderCell: commentRenderCell,
      ...options,
    }),
    editable: false, // Comments are edited via modal
    valueGetter: (value, row) => {
      if (!row) return "";
      const fieldValue = row[field];

      // Handle comment arrays - show preview or count
      if (Array.isArray(fieldValue)) {
        if (fieldValue.length === 0) return "";
        // Show latest comment text
        const latestComment = fieldValue[fieldValue.length - 1];
        return latestComment?.comment || "";
      }

      // Handle legacy string comments or other values
      return fieldValue || "";
    },
  };
};

// Create number column
const createNumberColumn = (field, headerName, options = {}) => ({
  ...createBaseColumn(field, headerName, {
    width: 100,
    align: "center",
    headerAlign: "center",
    ...options,
  }),
  type: "number",
  editable: true,
});

// Create text column
const createTextColumn = (field, headerName, options = {}) => ({
  ...createBaseColumn(field, headerName, options),
  type: "string",
  editable: true,
});

// PERFORMANCE OPTIMIZATION: Memoized control cell component
const ControlCell = React.memo(({ value }) => {
  if (value === null || value === undefined) return "";

  const diffDays = value;
  let displayText = "";
  let color = "";
  let isPastDue = false;

  if (diffDays < 0) {
    // Negative value means days to go (green/neutral)
    displayText = `${Math.abs(diffDays)} days to go`;
    color = "#666666"; // Gray for days to go
  } else if (diffDays > 0) {
    // Positive value means past due (red)
    displayText = `${diffDays} past due`;
    color = "#d32f2f"; // Red for past due
    isPastDue = true;
  } else {
    // Zero means on time (green)
    displayText = "On time";
    color = "#388e3c"; // Green for on time
  }

  return (
    <Box
      data-value={isPastDue ? "past-due" : ""}
      sx={{ width: "100%", height: "100%" }}
    >
      <Typography variant="body2" sx={{ color, fontWeight: 500 }}>
        {displayText}
      </Typography>
    </Box>
  );
});

ControlCell.displayName = "ControlCell";

// Helper function to parse date from various formats
const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string") {
    // Extract date part from ISO string to avoid timezone issues
    const datePart = value.split("T")[0];
    if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = datePart.split("-");
      const date = new Date(
        Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day))
      );
      return isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
  return null;
};

// Helper function to calculate days difference between two dates
const calculateDaysDifference = (actualDate, reqDate) => {
  if (!actualDate || !reqDate) return null;

  const actual = parseDateValue(actualDate);
  const req = parseDateValue(reqDate);

  if (!actual || !req) return null;

  // Calculate difference in milliseconds
  const diffMs = actual.getTime() - req.getTime();
  // Convert to days (round to nearest integer)
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  return diffDays;
};

// Create CONTROL column - calculates difference between ACTUAL and REQ dates
const createControlColumn = (
  field,
  headerName,
  handleOpenComments,
  options = {}
) => {
  // Derive REQ and ACTUAL field names from CONTROL or CONTROL II field name
  const isControlII = field.includes("(CONTROL II)");
  const reqField = isControlII
    ? field.replace("(CONTROL II)", "(REQ)")
    : field.replace("(CONTROL)", "(REQ)");
  const actualField = isControlII
    ? field.replace("(CONTROL II)", "(ACTUAL)")
    : field.replace("(CONTROL)", "(ACTUAL)");

  // Custom renderCell for control columns
  const controlRenderCell = (params) => {
    // PERFORMANCE OPTIMIZATION: Memoized control cell component
    return <ControlCell value={params?.value} />;
  };

  return {
    ...createBaseColumn(field, headerName, {
      width: 150,
      renderCell: controlRenderCell,
      ...options,
    }),
    editable: false, // CONTROL is calculated, not editable
    valueGetter: (value, row) => {
      if (!row) return null;

      // Get REQ and ACTUAL dates from the row
      const reqDate = row[reqField];
      const actualDate = row[actualField];

      // Calculate difference in days
      const diffDays = calculateDaysDifference(actualDate, reqDate);

      return diffDays; // Return numeric value for sorting/filtering
    },
    valueFormatter: (value, row, column, apiRef) => {
      if (value === null || value === undefined) return "";

      const diffDays = value;

      if (diffDays < 0) {
        // Negative value means days to go
        return `${Math.abs(diffDays)} days to go`;
      } else if (diffDays > 0) {
        // Positive value means past due
        return `${diffDays} past due`;
      } else {
        // Zero means on time
        return "On time";
      }
    },
  };
};

/**
 * Dynamically generate WIP columns based on actual row data
 * Uses displayName from backend for column headers
 * @param {Array} rows - Array of row data
 * @param {Function} handleOpenComments - Callback for opening comment modal
 * @param {boolean} isSupplierView - Whether this is supplier-facing platform (default: false)
 */
const SKIP_FIELDS = [
  "factoryName", "factory", "supplierName", "styleNumber",
  "vendorStyle", "rootsStyle", "po", "poNumber", "colour",
  "customer", "description", "units", "season", "category",
  "department", "statusRollup", "factoryId",
];

export const createWipColumnsDynamic = (
  rows,
  handleOpenComments,
  options = {}
) => {
  // Support both old boolean format and new options object format for backward compatibility
  const isSupplierView =
    typeof options === "boolean" ? options : options.readOnly || false;
  const {
    onCellSelection,
    onColumnHeaderCheckbox,
    isCellSelected,
    isColumnFullySelected,
    getSelectedCount,
    lockedState,
  } = typeof options === "object" && !Array.isArray(options) ? options : {};

  // Create a helper to add selection handlers to column options
  // For supplier view: conditionally add handlers based on field type and lock state
  const addSelectionHandlers = (fieldName, colOptions = {}) => {
    // If not supplier view, always add handlers
    if (!isSupplierView) {
      return {
        ...colOptions,
        onColumnHeaderCheckbox,
        isColumnFullySelected,
        onCellSelection,
        isCellSelected,
        getSelectedCount,
      };
    }

    // Supplier view: check if field should have selection handlers
    if (!fieldName) {
      // If no field name provided, don't add handlers (backward compatibility)
      return colOptions;
    }

    const fieldUpper = fieldName.toUpperCase();
    const isLocked = lockedState?.state === true;

    // STATUS fields: never have checkboxes (not editable)
    if (fieldUpper.includes("(STATUS)")) {
      return colOptions; // No handlers
    }

    // COMMENT fields: never have checkboxes (not editable)
    if (fieldUpper.includes("(COMMENT)")) {
      return colOptions; // No handlers
    }

    // REQ dates: only have checkboxes when NOT locked
    if (fieldUpper.includes("(REQ)")) {
      if (isLocked) {
        return colOptions; // No handlers when locked
      }
      // Add handlers when unlocked
      return {
        ...colOptions,
        onColumnHeaderCheckbox,
        isColumnFullySelected,
        onCellSelection,
        isCellSelected,
        getSelectedCount,
      };
    }

    // ACTUAL dates: always have checkboxes (always editable)
    if (fieldUpper.includes("(ACTUAL)")) {
      return {
        ...colOptions,
        onColumnHeaderCheckbox,
        isColumnFullySelected,
        onCellSelection,
        isCellSelected,
        getSelectedCount,
      };
    }

    // For other fields, don't add handlers in supplier view
    return colOptions;
  };
  if (!rows || rows.length === 0) {
    return [];
  }

  // Use first row to get displayName mappings
  const firstRow = rows[0];

  // Get all unique field names from rows
  const allFields = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      // Skip only specific internal fields, not all fields starting with underscore
      // Keep fields like "main_information_CATEGORY" and "fabric_approval: lab_dips_submit (REQ)"
      if (
        key !== "id" &&
        key !== "_id" && // Skip pure internal ID field
        key !== "actions" &&
        !key.endsWith("(DISPLAY_NAME)") && // Skip display name metadata fields
        !SKIP_FIELDS.includes(key)
      ) {
        allFields.add(key);
      }
    });
  });

  const fieldsArray = Array.from(allFields);

  // Debug: Log TC and comment fields
  const tcFields = fieldsArray.filter((f) => f.includes("tc_certificate"));
  const commentFields = fieldsArray.filter((f) => f.includes("(COMMENT)"));
  if (tcFields.length > 0) {
    console.log("🔍 [wipColumns] TC fields found:", tcFields);
  }
  if (commentFields.length > 0) {
    console.log(
      "🔍 [wipColumns] Comment fields found:",
      commentFields.slice(0, 5)
    );
  }

  const columns = [];

  // --- Extract unique filter values from rows ---
  const individualDepts = new Set();
  rows.forEach((r) => {
    if (r.department) {
      r.department.split(",").forEach((d) => {
        const trimmed = d.trim();
        if (trimmed) individualDepts.add(trimmed);
      });
    }
  });
  const uniqueDepartments = [...individualDepts].sort();

  const uniqueSeasons = [...new Set(rows.map((r) => r.season).filter(Boolean))].sort();

  // --- Custom department filter (handles comma-joined values) ---
  const departmentFilterOperators = getGridSingleSelectOperators().map((op) => ({
    ...op,
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value && filterItem.value !== 0) return null;
      return (value) => {
        if (!value) return false;
        const parts = value.split(",").map((d) => d.trim());
        if (op.value === "not") return !parts.includes(filterItem.value);
        return parts.includes(filterItem.value);
      };
    },
  }));

  // --- Add standalone columns ---
  columns.push({
    ...createBaseColumn("department", "Department", { width: 160 }),
    editable: false,
    filterable: true,
    type: "singleSelect",
    valueOptions: uniqueDepartments,
    filterOperators: departmentFilterOperators,
  });

  columns.push({
    ...createBaseColumn("season", "Season", { width: 140 }),
    editable: false,
    filterable: true,
    type: "singleSelect",
    valueOptions: uniqueSeasons,
  });

  // Standard stage order: main_information first, then material, then tc, then others
  const STANDARD_STAGE_ORDER = [
    { stageKey: "main_information", displayName: "Main Information" },
    {
      stageKey: "material_specification",
      displayName: "Material Specification",
    },
    { stageKey: "tc_certificate", displayName: "TC Certificate" },
    { stageKey: "fabric_approval", displayName: "Fabric Approval" },
    { stageKey: "trim_approval", displayName: "Trim Approval" },
    { stageKey: "sample_status", displayName: "Sample Status" },
    { stageKey: "testing", displayName: "Testing" },
    { stageKey: "production", displayName: "Production" },
    { stageKey: "inspection", displayName: "Inspection" },
    { stageKey: "logistics", displayName: "Logistics" },
  ];

  // Separate simple fields (from stepsInStage) and nested fields (from substages)
  // Simple fields format: "stageKey_fieldName" (e.g., "main_information_CATEGORY" or "tc_certificate_(MANDATORY) TC")
  // Nested fields format: "stageKey: substageKey (TYPE)" (e.g., "fabric_approval: lab_dips_submit (REQ)")
  const simpleFields = fieldsArray.filter((field) => {
    // Simple fields: contain underscore, no colon
    // Allow parentheses in simple fields (e.g., "tc_certificate_(MANDATORY) TC")
    // But exclude stage metadata fields like "stageKey_status", "stageKey_daysLate"
    return (
      field.includes("_") &&
      !field.includes(":") &&
      !field.endsWith("_status") &&
      !field.endsWith("_daysLate") &&
      !field.endsWith("_displayName") &&
      !field.endsWith("_id")
    );
  });
  const nestedFields = fieldsArray.filter(
    (field) => field.includes(":") && field.includes("(")
  );

  // Group simple fields by stageKey and process in STANDARD_STAGE_ORDER
  const simpleFieldsByStage = new Map();
  simpleFields.forEach((field) => {
    // Extract stageKey from field (everything before the last underscore that matches a known stage)
    let stageKey = null;
    for (const stage of STANDARD_STAGE_ORDER) {
      if (field.startsWith(`${stage.stageKey}_`)) {
        stageKey = stage.stageKey;
        break;
      }
    }
    // If no match found, try to extract from field name
    if (!stageKey) {
      const parts = field.split("_");
      // Try to match known stage keys
      for (let i = 1; i < parts.length; i++) {
        const possibleStageKey = parts.slice(0, i).join("_");
        if (STANDARD_STAGE_ORDER.some((s) => s.stageKey === possibleStageKey)) {
          stageKey = possibleStageKey;
          break;
        }
      }
    }

    if (stageKey) {
      if (!simpleFieldsByStage.has(stageKey)) {
        simpleFieldsByStage.set(stageKey, []);
      }
      simpleFieldsByStage.get(stageKey).push(field);
    }
  });

  // Process simple fields in STANDARD_STAGE_ORDER (maintain backend order within each stage)
  STANDARD_STAGE_ORDER.forEach((stage) => {
    const stageFields = simpleFieldsByStage.get(stage.stageKey) || [];
    // Process fields in their original order (no sorting)
    stageFields.forEach((field) => {
      // Extract field name by removing the stage key prefix
      let fieldName = field;
      if (field.startsWith(`${stage.stageKey}_`)) {
        fieldName = field.substring(stage.stageKey.length + 1); // +1 for the underscore
      } else {
        // Fallback: try to extract field name
        const lastUnderscoreIndex = field.lastIndexOf("_");
        if (lastUnderscoreIndex > 0) {
          const possibleStageKey = field.substring(0, lastUnderscoreIndex);
          if (
            STANDARD_STAGE_ORDER.some((s) => s.stageKey === possibleStageKey)
          ) {
            fieldName = field.substring(lastUnderscoreIndex + 1);
          } else {
            // Fallback: take everything after first underscore
            const parts = field.split("_");
            fieldName = parts.slice(1).join("_");
          }
        }
      }

      // Determine column type
      if (field.includes("UNITS") || fieldName === "UNITS") {
        columns.push(
          createNumberColumn(
            field,
            fieldName,
            addSelectionHandlers(field, { width: 100 })
          )
        );
      } else if (
        field.includes("DATE") ||
        field.includes("TC") ||
        field.includes("TIER")
      ) {
        columns.push(
          createDateColumn(
            field,
            fieldName,
            handleOpenComments,
            addSelectionHandlers(field, { width: 150 })
          )
        );
      } else {
        columns.push(
          createTextColumn(
            field,
            fieldName,
            addSelectionHandlers(field, { width: 150 })
          )
        );
      }
    });
  });

  // Group nested fields by substage (everything before the last "(")
  const substageGroups = new Map();
  nestedFields.forEach((field) => {
    const lastParen = field.lastIndexOf("(");
    const substageKey = field.substring(0, lastParen).trim();

    if (!substageGroups.has(substageKey)) {
      substageGroups.set(substageKey, []);
    }
    substageGroups.get(substageKey).push(field);
  });

  // Group substages by stageKey and process in STANDARD_STAGE_ORDER
  const substagesByStage = new Map();
  Array.from(substageGroups.entries()).forEach(([substageKey, fields]) => {
    const stageKey = substageKey.split(":")[0].trim();
    if (!substagesByStage.has(stageKey)) {
      substagesByStage.set(stageKey, []);
    }
    substagesByStage.get(stageKey).push([substageKey, fields]);
  });

  // Process substages in STANDARD_STAGE_ORDER (maintain backend order within each stage)
  const processedSubstages = [];
  STANDARD_STAGE_ORDER.forEach((stage) => {
    const stageSubstages = substagesByStage.get(stage.stageKey) || [];
    // Process substages in their original order (no sorting)
    processedSubstages.push(...stageSubstages);
  });

  // Also include any substages from stages not in STANDARD_STAGE_ORDER (maintain backend order)
  Array.from(substagesByStage.entries()).forEach(([stageKey, substages]) => {
    if (!STANDARD_STAGE_ORDER.some((s) => s.stageKey === stageKey)) {
      processedSubstages.push(...substages);
    }
  });

  // Add columns for each substage in order: REQ, ACTUAL, CONTROL, STATUS, COMMENT
  const fieldTypeOrder = [
    "(REQ)",
    "(ACTUAL)",
    "(CONTROL)",
    "(CONTROL II)",
    "(DAYS)",
    "(STATUS)",
    "(APPROVAL DATE)",
    "(COMMENT)",
  ];

  processedSubstages.forEach(([substageKey, fields]) => {
    // Get displayName from first row
    const displayNameField = `${substageKey} (DISPLAY_NAME)`;
    const displayName =
      firstRow[displayNameField] || substageKey.split(":")[1].trim();

    fieldTypeOrder.forEach((type) => {
      const field = fields.find((f) => f.includes(type));
      if (field) {
        // Extract field type for header
        const fieldType = type.replace(/[()]/g, ""); // Remove parentheses
        const headerName = `${displayName} (${fieldType})`;

        if (type === "(CONTROL)" || type === "(CONTROL II)") {
          // CONTROL columns are calculated from REQ and ACTUAL dates
          columns.push(
            createControlColumn(
              field,
              headerName,
              handleOpenComments,
              addSelectionHandlers(field)
            )
          );
        } else if (
          type === "(REQ)" ||
          type === "(ACTUAL)" ||
          type === "(DAYS)" ||
          type === "(APPROVAL DATE)"
        ) {
          columns.push(
            createDateColumn(
              field,
              headerName,
              handleOpenComments,
              addSelectionHandlers(field)
            )
          );
        } else if (type === "(STATUS)") {
          // Supplier-facing platform: use chip display (read-only)
          if (isSupplierView) {
            columns.push(
              createStatusChipColumn(field, headerName, addSelectionHandlers(field))
            );
          } else {
            // Check if it's a testing field (use TEST_CONCLUSION_OPTIONS)
            if (field.includes("testing") || field.includes("test")) {
              columns.push(
                createStatusColumn(field, headerName, {
                  valueOptions: TEST_CONCLUSION_OPTIONS,
                  ...addSelectionHandlers(field),
                })
              );
            } else {
              columns.push(
                createApprovalDropdownColumn(
                  field,
                  headerName,
                  addSelectionHandlers(field)
                )
              );
            }
          }
        } else if (type === "(COMMENT)") {
          columns.push(
            createTextColumnWithComments(
              field,
              headerName,
              handleOpenComments,
              addSelectionHandlers(field, {
                width: 200,
              })
            )
          );
        }
      }
    });
  });

  return columns;
};

/**
 * Create WIP columns based on NEW backend structure
 * DEPRECATED: Use createWipColumnsDynamic instead
 * Kept for backward compatibility
 */
export const createWipColumns = (handleOpenComments) => [
  // MAIN INFORMATION (Simple fields - no nested objects)
  createTextColumn("CATEGORY", "Category", { width: 120 }),
  createTextColumn("VENDOR STYLE #", "Vendor Style #", { width: 150 }),
  createTextColumn("ROOTS STYLE #", "Roots Style #", { width: 150 }),
  createTextColumn("PO #", "PO #", { width: 120 }),
  createTextColumn("CUSTOMER (Select from drop down)", "Customer", {
    width: 150,
  }),
  createNumberColumn("UNITS", "Units", { width: 100 }),
  createTextColumn("DESCRIPTION", "Description", { width: 200 }),
  createTextColumn("COLOUR", "Colour", { width: 120 }),
  createTextColumn("Factory", "Factory", { width: 200 }),

  // TC CERTIFICATE (Simple fields)
  createTextColumn("(MANDATORY) TC", "TC", { width: 150 }),
  createDateColumn(
    "(MANDATORY) TIER 1 - CERTIFICATE DATE TC for Garment - Please provide the date when the certificate was issued after the PO shipment",
    "TC Cert Date",
    handleOpenComments,
    { width: 150 }
  ),
  createTextColumn("(MANDATORY) Ref # of the Certificate", "Cert Ref #", {
    width: 150,
  }),

  // MATERIAL SPECIFICATION (Simple fields)
  createTextColumn(
    "(MANDATORY) PRIMARY (Specify Content and Weight - For e.g. 80% Cotton 20% Polyester, 360 GSM, Fleece)",
    "Primary Material",
    { width: 250 }
  ),
  createTextColumn("2ND", "2nd Material", { width: 150 }),
  createTextColumn("3RD", "3rd Material", { width: 150 }),
  createTextColumn("(MANDATORY) CONTRAST BLOCKING COLOR", "Contrast Color", {
    width: 150,
  }),
  createTextColumn(
    "(MANDATORY) LOGO / PRINT APPLICATION Use the correct terminology. Please see the provided options above the column",
    "Logo/Print",
    { width: 200 }
  ),

  // FABRIC APPROVAL (Nested objects)
  // LAB DIPS/STRIKE OFF/ KNIT DOWN
  createDateColumn(
    "fabric_approval: LAB DIPS/STRIKE OFF/ KNIT DOWN (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "fabric_approval: LAB DIPS/STRIKE OFF/ KNIT DOWN (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "fabric_approval: LAB DIPS/STRIKE OFF/ KNIT DOWN (CONTROL)",
    null,
    handleOpenComments
  ),
  createApprovalDropdownColumn(
    "fabric_approval: LAB DIPS/STRIKE OFF/ KNIT DOWN (STATUS)",
    null
  ),
  createTextColumnWithComments(
    "fabric_approval: LAB DIPS/STRIKE OFF/ KNIT DOWN (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // BULK FABRIC
  createDateColumn(
    "fabric_approval: BULK FABRIC (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "fabric_approval: BULK FABRIC (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "fabric_approval: BULK FABRIC (CONTROL)",
    null,
    handleOpenComments
  ),
  createApprovalDropdownColumn("fabric_approval: BULK FABRIC (STATUS)", null),
  createTextColumnWithComments(
    "fabric_approval: BULK FABRIC (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // TRIM APPROVAL (Nested objects)
  // TRIM SMPL
  createDateColumn("trim_approval: TRIM SMPL (REQ)", null, handleOpenComments),
  createDateColumn(
    "trim_approval: TRIM SMPL (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "trim_approval: TRIM SMPL (CONTROL)",
    null,
    handleOpenComments
  ),
  createApprovalDropdownColumn("trim_approval: TRIM SMPL (STATUS)", null),
  createTextColumnWithComments(
    "trim_approval: TRIM SMPL (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // BULK TRIM
  createDateColumn("trim_approval: BULK TRIM (REQ)", null, handleOpenComments),
  createDateColumn(
    "trim_approval: BULK TRIM (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "trim_approval: BULK TRIM (CONTROL)",
    null,
    handleOpenComments
  ),
  createApprovalDropdownColumn("trim_approval: BULK TRIM (STATUS)", null),
  createTextColumnWithComments(
    "trim_approval: BULK TRIM (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // SAMPLE STATUS (Nested objects)
  // FIT
  createDateColumn("sample_status: FIT (REQ)", null, handleOpenComments),
  createDateColumn("sample_status: FIT (ACTUAL)", null, handleOpenComments),
  createControlColumn("sample_status: FIT (CONTROL)", null, handleOpenComments),
  createApprovalDropdownColumn("sample_status: FIT (STATUS)", null),
  createTextColumnWithComments(
    "sample_status: FIT (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // PP
  createDateColumn("sample_status: PP (REQ)", null, handleOpenComments),
  createDateColumn("sample_status: PP (ACTUAL)", null, handleOpenComments),
  createControlColumn("sample_status: PP (CONTROL)", null, handleOpenComments),
  createApprovalDropdownColumn("sample_status: PP (STATUS)", null),
  createTextColumnWithComments(
    "sample_status: PP (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // TOP ARRIVAL DEADLINE
  createDateColumn(
    "sample_status: TOP ARRIVAL DEADLINE (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "sample_status: TOP ARRIVAL DEADLINE (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "sample_status: TOP ARRIVAL DEADLINE (CONTROL)",
    null,
    handleOpenComments
  ),
  createApprovalDropdownColumn(
    "sample_status: TOP ARRIVAL DEADLINE (STATUS)",
    null
  ),
  createTextColumnWithComments(
    "sample_status: TOP ARRIVAL DEADLINE (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // MARKETING TOP ARRIVAL DEADLINE
  createDateColumn(
    "sample_status: MARKETING TOP ARRIVAL DEADLINE (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "sample_status: MARKETING TOP ARRIVAL DEADLINE (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "sample_status: MARKETING TOP ARRIVAL DEADLINE (CONTROL)",
    null,
    handleOpenComments
  ),
  createApprovalDropdownColumn(
    "sample_status: MARKETING TOP ARRIVAL DEADLINE (STATUS)",
    null
  ),
  createTextColumnWithComments(
    "sample_status: MARKETING TOP ARRIVAL DEADLINE (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // TESTING (Nested objects)
  // FABRIC TESTING DATE
  createDateColumn(
    "testing: FABRIC TESTING DATE (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "testing: FABRIC TESTING DATE (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "testing: FABRIC TESTING DATE (CONTROL)",
    null,
    handleOpenComments
  ),
  createStatusColumn(
    "testing: FABRIC TESTING DATE (STATUS)",
    null,
    TEST_CONCLUSION_OPTIONS
  ),
  createTextColumnWithComments(
    "testing: FABRIC TESTING DATE (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // GARMENT TESTING DATE
  createDateColumn(
    "testing: GARMENT TESTING DATE (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "testing: GARMENT TESTING DATE (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "testing: GARMENT TESTING DATE (CONTROL)",
    null,
    handleOpenComments
  ),
  createStatusColumn(
    "testing: GARMENT TESTING DATE (STATUS)",
    null,
    TEST_CONCLUSION_OPTIONS
  ),
  createTextColumnWithComments(
    "testing: GARMENT TESTING DATE (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // PRODUCTION (Nested objects)
  // CUT DATE
  createDateColumn("production: CUT DATE (REQ)", null, handleOpenComments),
  createDateColumn("production: CUT DATE (ACTUAL)", null, handleOpenComments),
  createControlColumn(
    "production: CUT DATE (CONTROL)",
    null,
    handleOpenComments
  ),
  createApprovalDropdownColumn("production: CUT DATE (STATUS)", null),
  createTextColumnWithComments(
    "production: CUT DATE (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // SEWING
  createDateColumn("production: SEWING (REQ)", null, handleOpenComments),
  createDateColumn("production: SEWING (ACTUAL)", null, handleOpenComments),
  createControlColumn("production: SEWING (CONTROL)", null, handleOpenComments),
  createApprovalDropdownColumn("production: SEWING (STATUS)", null),
  createTextColumnWithComments(
    "production: SEWING (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // INSPECTION (Nested objects)
  // 3RD PARTY FINAL INSPECTION DATE
  createDateColumn(
    "inspection: 3RD PARTY FINAL INSPECTION DATE (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "inspection: 3RD PARTY FINAL INSPECTION DATE (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "inspection: 3RD PARTY FINAL INSPECTION DATE (CONTROL)",
    null,
    handleOpenComments
  ),
  createApprovalDropdownColumn(
    "inspection: 3RD PARTY FINAL INSPECTION DATE (STATUS)",
    null
  ),
  createTextColumnWithComments(
    "inspection: 3RD PARTY FINAL INSPECTION DATE (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // LOGISTICS DATES (Nested objects)
  // DATE TO BOOK THE VESSEL
  createDateColumn(
    "logistics_dates: DATE TO BOOK THE VESSEL (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "logistics_dates: DATE TO BOOK THE VESSEL (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "logistics_dates: DATE TO BOOK THE VESSEL (CONTROL)",
    null,
    handleOpenComments
  ),
  {
    field: "logistics_dates: DATE TO BOOK THE VESSEL (DAYS)",
    headerName: "DATE TO BOOK THE VESSEL (DAYS)",
    width: 100,
    align: "center",
    headerAlign: "center",
    editable: true,
  },
  createApprovalDropdownColumn(
    "logistics_dates: DATE TO BOOK THE VESSEL (STATUS)",
    null
  ),
  createTextColumnWithComments(
    "logistics_dates: DATE TO BOOK THE VESSEL (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // CARGO HAND OVER
  createDateColumn(
    "logistics_dates: CARGO HAND OVER (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "logistics_dates: CARGO HAND OVER (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "logistics_dates: CARGO HAND OVER (CONTROL)",
    null,
    handleOpenComments
  ),
  {
    field: "logistics_dates: CARGO HAND OVER (DAYS)",
    headerName: "CARGO HAND OVER (DAYS)",
    width: 100,
    align: "center",
    headerAlign: "center",
    editable: true,
  },
  createApprovalDropdownColumn(
    "logistics_dates: CARGO HAND OVER (STATUS)",
    null
  ),
  createDateColumn(
    "logistics_dates: CARGO HAND OVER (APPROVAL DATE)",
    null,
    handleOpenComments
  ),
  createTextColumnWithComments(
    "logistics_dates: CARGO HAND OVER (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // ON BOARD END
  createDateColumn(
    "logistics_dates: ON BOARD END (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "logistics_dates: ON BOARD END (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "logistics_dates: ON BOARD END (CONTROL)",
    null,
    handleOpenComments
  ),
  {
    field: "logistics_dates: ON BOARD END (DAYS)",
    headerName: "ON BOARD END (DAYS)",
    width: 100,
    align: "center",
    headerAlign: "center",
    editable: true,
  },
  createApprovalDropdownColumn("logistics_dates: ON BOARD END (STATUS)", null),
  createDateColumn(
    "logistics_dates: ON BOARD END (APPROVAL DATE)",
    null,
    handleOpenComments
  ),
  createTextColumnWithComments(
    "logistics_dates: ON BOARD END (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // IN DC END Cancel Date
  createDateColumn(
    "logistics_dates: IN DC END Cancel Date (REQ)",
    null,
    handleOpenComments
  ),
  createDateColumn(
    "logistics_dates: IN DC END Cancel Date (ACTUAL)",
    null,
    handleOpenComments
  ),
  createControlColumn(
    "logistics_dates: IN DC END Cancel Date (CONTROL)",
    null,
    handleOpenComments
  ),
  {
    field: "logistics_dates: IN DC END Cancel Date (DAYS)",
    headerName: "IN DC END Cancel Date (DAYS)",
    width: 100,
    align: "center",
    headerAlign: "center",
    editable: true,
  },
  createApprovalDropdownColumn(
    "logistics_dates: IN DC END Cancel Date (STATUS)",
    null
  ),
  createDateColumn(
    "logistics_dates: IN DC END Cancel Date (APPROVAL DATE)",
    null,
    handleOpenComments
  ),
  createTextColumnWithComments(
    "logistics_dates: IN DC END Cancel Date (COMMENT)",
    null,
    handleOpenComments,
    { width: 200 }
  ),

  // GENERAL COMMENTS (Simple field)
  createTextColumnWithComments(
    "COMMENTS",
    "General Comments",
    handleOpenComments,
    { width: 300 }
  ),
];

export default createWipColumns;
